(function () {
  "use strict";

  function safeText(value) {
    if (value === null || value === undefined) return "";
    return String(value);
  }

  function toNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function normalizePos(raw) {
    if (raw === null || raw === undefined) return "";
    return String(raw).replace(/[()\s]/g, "").toUpperCase();
  }

  function cleanGroupName(name) {
    return safeText(name).replace(/^Main group\s*\d+\s*:\s*/i, "").trim();
  }

  function cleanDiagramTitle(title, illustrationId) {
    var out = safeText(title);
    var id = safeText(illustrationId);
    if (id) {
      var prefix = new RegExp("^" + id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\s*:\\s*", "i");
      out = out.replace(prefix, "");
    }
    return out.trim();
  }

  // Optional per-group icon URL overrides by code.
  // If blank, runtime uses icon_url from pet-index.json (uploaded from local icons/<group_code>/ folder).
  var GROUP_ICON_URL_BY_CODE = {
    "0": "",
    "1": "",
    "2": "",
    "3": "",
    "4": "",
    "5": "",
    "6": "",
    "7": "",
    "8": "",
    "9": ""
  };

  function collectGroupIconCandidates(groupCode, groupIconUrl) {
    var code = safeText(groupCode);
    var candidates = [];

    function addCandidate(url) {
      var u = safeText(url).trim();
      if (!u) return;
      if (candidates.indexOf(u) === -1) candidates.push(u);
      var queryPos = u.indexOf("?");
      if (queryPos > 0) {
        var noQuery = u.slice(0, queryPos);
        if (candidates.indexOf(noQuery) === -1) candidates.push(noQuery);
      }
    }

    addCandidate(GROUP_ICON_URL_BY_CODE[code]);
    addCandidate(groupIconUrl);
    return candidates;
  }

  function createGroupIcon(groupCode, groupName, groupIconUrl) {
    var iconWrap = createNode("div", "diagram-catalog__group-icon");
    var code = safeText(groupCode);
    var candidates = collectGroupIconCandidates(code, groupIconUrl);

    function showCodeFallback() {
      iconWrap.classList.remove("has-image");
      while (iconWrap.firstChild) iconWrap.removeChild(iconWrap.firstChild);
      var fallback = createNode("span", "diagram-catalog__group-icon-code", code || "G");
      fallback.setAttribute("aria-hidden", "true");
      iconWrap.appendChild(fallback);
    }

    if (!candidates.length) {
      showCodeFallback();
      return iconWrap;
    }

    var img = createNode("img", "diagram-catalog__group-icon-image");
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";
    img.setAttribute("aria-hidden", "true");
    img.addEventListener("load", function () {
      iconWrap.classList.add("has-image");
    });

    var i = 0;
    function loadNextCandidate() {
      if (i >= candidates.length) {
        showCodeFallback();
        return;
      }
      img.src = candidates[i++];
    }

    img.addEventListener("error", loadNextCandidate);
    iconWrap.appendChild(img);
    loadNextCandidate();
    return iconWrap;
  }

  function compareAlphaAsc(a, b) {
    return safeText(a).localeCompare(safeText(b), undefined, { sensitivity: "base", numeric: true });
  }

  function dedupeCallouts(callouts) {
    var byId = new Map();
    for (var i = 0; i < callouts.length; i++) {
      var c = callouts[i] || {};
      var idNorm = normalizePos(c.id);
      if (!idNorm) continue;

      var existing = byId.get(idNorm);
      if (!existing) {
        byId.set(idNorm, c);
        continue;
      }

      var hasExistingScore = typeof existing.score === "number";
      var hasCurrentScore = typeof c.score === "number";
      if (hasCurrentScore && hasExistingScore && c.score > existing.score) {
        byId.set(idNorm, c);
      } else if (hasCurrentScore && !hasExistingScore) {
        byId.set(idNorm, c);
      }
    }
    return Array.from(byId.values());
  }

  function createNode(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  function parseJSON(text, fallback) {
    try {
      return JSON.parse(text);
    } catch (err) {
      return fallback;
    }
  }

  function buildFetchCandidates(url, preferFresh) {
    var candidates = [];
    var base = safeText(url).trim();
    if (!base) return candidates;

    function addCandidate(u) {
      if (!u) return;
      if (candidates.indexOf(u) === -1) candidates.push(u);
    }

    addCandidate(base);
    var qPos = base.indexOf("?");
    if (qPos > 0) addCandidate(base.slice(0, qPos));

    if (preferFresh) {
      var stamp = String(Date.now());
      var withTs = base + (base.indexOf("?") === -1 ? "?" : "&") + "_ts=" + encodeURIComponent(stamp);
      addCandidate(withTs);
      if (qPos > 0) addCandidate(base.slice(0, qPos) + "?_ts=" + encodeURIComponent(stamp));
    }

    return candidates;
  }

  function loadJSON(url, opts) {
    var options = opts || {};
    var retries = Math.max(1, toNumber(options.retries, 1));
    var preferFresh = options.preferFresh === true;
    var candidates = buildFetchCandidates(url, preferFresh);
    if (!candidates.length) return Promise.reject(new Error("Missing JSON URL"));

    var lastErr = null;

    function fetchCandidate(candidate) {
      return fetch(candidate, {
        credentials: "same-origin",
        cache: preferFresh ? "no-store" : "default"
      }).then(function (res) {
        if (!res.ok) throw new Error("Failed to fetch " + candidate + " status=" + res.status);
        return res.text().then(function (text) {
          var parsed = parseJSON(text, null);
          if (parsed === null) throw new Error("Invalid JSON from " + candidate);
          return parsed;
        });
      });
    }

    function runAttempt(attempt) {
      var idx = 0;
      function nextCandidate() {
        if (idx >= candidates.length) {
          if (attempt >= retries) throw lastErr || new Error("Failed to load JSON: " + safeText(url));
          return runAttempt(attempt + 1);
        }
        var candidate = candidates[idx++];
        return fetchCandidate(candidate).catch(function (err) {
          lastErr = err;
          return nextCandidate();
        });
      }
      return nextCandidate();
    }

    return runAttempt(1);
  }

  function searchByPartNumber(partNumber) {
    window.open("/search?q=" + encodeURIComponent(partNumber || ""), "_blank", "noopener");
  }

  function compactPartNumber(partNumber) {
    return safeText(partNumber).replace(/\s+/g, "");
  }

  function updateCatalogQuery(modelCode, groupCode) {
    var params = new URLSearchParams(window.location.search);
    if (modelCode) params.set("model", modelCode);
    else params.delete("model");

    if (groupCode) params.set("group", groupCode);
    else params.delete("group");

    var qs = params.toString();
    var url = window.location.pathname + (qs ? "?" + qs : "");
    window.history.replaceState({}, "", url);
  }

  function buildViewerHref(viewerBase, illustrationId, diagramKey, modelCode, groupCode) {
    var base = viewerBase + encodeURIComponent(illustrationId || "");
    var extra = [];
    if (diagramKey) extra.push("k=" + encodeURIComponent(diagramKey));
    if (modelCode) extra.push("model=" + encodeURIComponent(modelCode));
    if (groupCode) extra.push("group=" + encodeURIComponent(groupCode));
    if (!extra.length) return base;
    return base + (base.indexOf("?") === -1 ? "?" : "&") + extra.join("&");
  }

  function setSelectOptions(selectEl, placeholder, options) {
    while (selectEl.options.length) selectEl.remove(0);
    var ph = createNode("option", "", placeholder);
    ph.value = "";
    selectEl.appendChild(ph);

    options.forEach(function (optData) {
      var opt = createNode("option", "", safeText(optData.label));
      opt.value = safeText(optData.value);
      selectEl.appendChild(opt);
    });
  }

  function initCatalog(root) {
    var indexUrl = root.getAttribute("data-index-url") || "";
    var viewerBase = root.getAttribute("data-viewer-base") || "/pages/diagram-viewer?id=";
    var showPreviews = root.getAttribute("data-show-previews") === "true";
    var modelSelect = root.querySelector("[data-model-select]");
    var groupSelect = root.querySelector("[data-group-select]");
    var metaEl = root.querySelector("[data-catalog-meta]");
    var groupGridEl = root.querySelector("[data-group-grid]");
    var workspaceEl = root.querySelector("[data-diagram-workspace]");
    var sidebarEl = root.querySelector("[data-group-sidebar]");
    var diagramGridEl = root.querySelector("[data-diagram-grid]");
    var listWrap = root.querySelector("[data-diagram-list]");

    if (!indexUrl) {
      listWrap.textContent = "Missing index URL.";
      return;
    }

    var params = new URLSearchParams(window.location.search);
    var preModel = safeText(params.get("model"));
    var preGroup = safeText(params.get("group"));

    loadJSON(indexUrl, { preferFresh: true, retries: 2 })
      .then(function (index) {
        var models = Array.isArray(index && index.models) ? index.models.slice() : [];
        models.sort(function (a, b) {
          return toNumber(a.sort_order, 0) - toNumber(b.sort_order, 0);
        });

        setSelectOptions(
          modelSelect,
          "Select model",
          models.map(function (m) {
            return { label: safeText(m.name), value: safeText(m.code) };
          })
        );
        setSelectOptions(groupSelect, "Select main group", []);
        listWrap.textContent = "";
        groupGridEl.innerHTML = "";
        sidebarEl.innerHTML = "";
        diagramGridEl.innerHTML = "";
        workspaceEl.hidden = true;

        function setActiveGroupCard(groupCode) {
          var cards = groupGridEl.querySelectorAll(".diagram-catalog__group-card");
          for (var i = 0; i < cards.length; i++) {
            var card = cards[i];
            var code = safeText(card.getAttribute("data-group-code"));
            var isActive = !!groupCode && code === groupCode;
            card.classList.toggle("is-active", isActive);
            card.setAttribute("aria-pressed", isActive ? "true" : "false");
          }
        }

        function renderGroupsForModel(modelCode, selectedGroupCode) {
          var model = models.find(function (x) {
            return safeText(x.code) === modelCode;
          });

          if (!model) {
            setSelectOptions(groupSelect, "Select main group", []);
            listWrap.textContent = "";
            groupGridEl.innerHTML = "";
            workspaceEl.hidden = true;
            return;
          }

          var groups = Array.isArray(model.main_groups) ? model.main_groups.slice() : [];
          groups.sort(function (a, b) {
            return compareAlphaAsc(cleanGroupName(a.group_name), cleanGroupName(b.group_name));
          });

          setSelectOptions(
            groupSelect,
            "Select main group",
            groups.map(function (g) {
              var count = Array.isArray(g.diagrams) ? g.diagrams.length : 0;
              return {
                label: cleanGroupName(g.group_name) + " (" + count + ")",
                value: safeText(g.group_code)
              };
            })
          );

          metaEl.textContent = "Model: " + safeText(model.name) + " | Main groups: " + groups.length;
          renderGroupTiles(modelCode, groups);
          setActiveGroupCard(selectedGroupCode || "");
          workspaceEl.hidden = true;
          diagramGridEl.innerHTML = "";
          sidebarEl.innerHTML = "";
        }

        function renderGroupTiles(modelCode, groups) {
          groupGridEl.innerHTML = "";
          if (!groups.length) {
            groupGridEl.textContent = "No main groups available.";
            return;
          }

          groups.forEach(function (g) {
            var groupCode = safeText(g.group_code);
            var count = Array.isArray(g.diagrams) ? g.diagrams.length : 0;
            var btn = createNode("button", "diagram-catalog__group-card");
            btn.type = "button";
            btn.setAttribute("data-group-code", groupCode);
            btn.setAttribute("aria-pressed", "false");

            var icon = createGroupIcon(groupCode, g.group_name, g.icon_url);
            var name = createNode("div", "diagram-catalog__group-name", cleanGroupName(g.group_name));
            var meta = createNode("div", "diagram-catalog__group-count", count + " diagrams");
            btn.appendChild(icon);
            btn.appendChild(name);
            btn.appendChild(meta);

            btn.addEventListener("click", function () {
              groupSelect.value = groupCode;
              groupSelect.dispatchEvent(new Event("change"));
            });

            groupGridEl.appendChild(btn);
          });
        }

        function renderGroupWorkspace(modelCode, modelName, group) {
          var groupCode = safeText(group.group_code);
          var diagrams = Array.isArray(group.diagrams) ? group.diagrams.slice() : [];
          diagrams.sort(function (a, b) {
            return compareAlphaAsc(cleanDiagramTitle(a.title, a.illustration_id), cleanDiagramTitle(b.title, b.illustration_id));
          });

          workspaceEl.hidden = false;
          sidebarEl.innerHTML = "";
          diagramGridEl.innerHTML = "";
          listWrap.textContent = "";

          var sideTitle = createNode("h3", "diagram-catalog__sidebar-title", cleanGroupName(group.group_name));
          sidebarEl.appendChild(sideTitle);
          var sideList = createNode("ul", "diagram-catalog__sidebar-list");
          sidebarEl.appendChild(sideList);

          var heading = createNode(
            "p",
            "diagram-catalog__workspace-meta",
            "Diagrams for " + safeText(modelName) + " / " + cleanGroupName(group.group_name)
          );
          diagramGridEl.appendChild(heading);
          var grid = createNode("div", "diagram-catalog__cards");
          diagramGridEl.appendChild(grid);

          diagrams.forEach(function (d) {
            var id = safeText(d.illustration_id);
            var diagramKey = safeText(d.diagram_key);
            var title = cleanDiagramTitle(d.title, id);
            var href = buildViewerHref(viewerBase, id, diagramKey, modelCode, groupCode);

            var sideItem = createNode("li", "diagram-catalog__sidebar-item");
            var sideLink = createNode("a", "diagram-catalog__sidebar-link", title);
            sideLink.href = href;
            sideItem.appendChild(sideLink);
            sideList.appendChild(sideItem);

            var card = createNode("a", "diagram-catalog__diagram-card");
            card.href = href;
            var imageWrap = createNode("div", "diagram-catalog__diagram-image-wrap");
            var img = createNode("img", "diagram-catalog__diagram-image");
            img.alt = id + " " + title;
            img.width = 600;
            img.height = 420;
            if (showPreviews) {
              var previewUrl = safeText(d.preview_image_url);
              if (previewUrl) {
                img.src = previewUrl;
              } else if (d.data_url) {
                loadJSON(d.data_url)
                  .then(function (dj) {
                    var src = safeText(dj && dj.diagram_url);
                    if (src) img.src = src;
                  })
                  .catch(function () {});
              }
            }
            imageWrap.appendChild(img);
            card.appendChild(imageWrap);
            card.appendChild(createNode("div", "diagram-catalog__diagram-title", title));
            grid.appendChild(card);
          });
        }

        modelSelect.addEventListener("change", function () {
          var modelCode = modelSelect.value;
          renderGroupsForModel(modelCode, "");
          updateCatalogQuery(modelCode, "");
        });

        groupSelect.addEventListener("change", function () {
          var modelCode = modelSelect.value;
          var groupCode = groupSelect.value;
          updateCatalogQuery(modelCode, groupCode);
          setActiveGroupCard(groupCode);

          listWrap.textContent = "";
          if (!modelCode || !groupCode) return;

          var model = models.find(function (x) {
            return safeText(x.code) === modelCode;
          });
          if (!model) return;

          var groups = Array.isArray(model.main_groups) ? model.main_groups : [];
          var group = groups.find(function (x) {
            return safeText(x.group_code) === groupCode;
          });
          if (!group) return;

          renderGroupWorkspace(modelCode, safeText(model.name), group);
        });

        // URL prefilter support: /pages/catalog?model=porsche-944&group=0
        if (preModel) {
          modelSelect.value = preModel;
          renderGroupsForModel(preModel, preGroup);
          if (preGroup) {
            groupSelect.value = preGroup;
            groupSelect.dispatchEvent(new Event("change"));
          }
        } else if (models.length) {
          modelSelect.value = safeText(models[0].code);
          modelSelect.dispatchEvent(new Event("change"));
        }
      })
      .catch(function (err) {
        listWrap.textContent = "Failed to load catalog index. " + safeText(err && err.message);
      });
  }

  function initViewer(root) {
    var diagramJsonBase = root.getAttribute("data-diagram-json-base") || "";
    var indexUrl = root.getAttribute("data-index-url") || "";
    var catalogUrl = root.getAttribute("data-catalog-url") || "/pages/catalog";
    var autoSearch = root.getAttribute("data-auto-search") === "true";
    var hotspotShape = root.getAttribute("data-hotspot-shape") || "circle";

    root.classList.toggle("diagram-viewer--square-hotspots", hotspotShape === "square");

    var titleEl = root.querySelector("[data-diagram-title]");
    var imageEl = root.querySelector("[data-diagram-image]");
    var hotspotLayer = root.querySelector("[data-hotspots]");
    var tableBody = root.querySelector("[data-parts-body]");
    var statusEl = root.querySelector("[data-status]");
    var breadcrumbPathEl = root.querySelector("[data-breadcrumb-path]");
    var catalogLink = root.querySelector("[data-nav-catalog]");
    var prevLink = root.querySelector("[data-nav-prev]");
    var nextLink = root.querySelector("[data-nav-next]");
    var jumpModel = root.querySelector("[data-jump-model]");
    var jumpGroup = root.querySelector("[data-jump-group]");
    var jumpDiagram = root.querySelector("[data-jump-diagram]");
    var groupIconsWrap = root.querySelector("[data-group-icons]");

    var params = new URLSearchParams(window.location.search);
    var diagramKey = safeText(params.get("k"));
    var diagramId = safeText(params.get("id"));

    function diagramUrlFromBase(keyOrId) {
      var base = diagramJsonBase.replace(/\/+$/, "");
      return base + "/" + encodeURIComponent(keyOrId) + ".json";
    }

    function buildViewerSelfHref(id, key, modelCode, groupCode) {
      var qp = new URLSearchParams();
      if (id) qp.set("id", id);
      if (key) qp.set("k", key);
      if (modelCode) qp.set("model", modelCode);
      if (groupCode) qp.set("group", groupCode);
      return window.location.pathname + "?" + qp.toString();
    }

    function buildCatalogHref(modelCode, groupCode) {
      var qp = new URLSearchParams();
      if (modelCode) qp.set("model", modelCode);
      if (groupCode) qp.set("group", groupCode);
      if (!qp.toString()) return catalogUrl;
      return catalogUrl + (catalogUrl.indexOf("?") === -1 ? "?" : "&") + qp.toString();
    }

    function setNavLink(linkEl, item, modelCode, groupCode) {
      if (!linkEl) return;
      if (!item) {
        linkEl.removeAttribute("href");
        linkEl.classList.add("is-disabled");
        linkEl.setAttribute("aria-disabled", "true");
        return;
      }

      linkEl.href = buildViewerSelfHref(
        safeText(item.illustration_id),
        safeText(item.diagram_key),
        modelCode,
        groupCode
      );
      linkEl.classList.remove("is-disabled");
      linkEl.removeAttribute("aria-disabled");
    }

    function renderGroupIcons(index, modelCode, groupCode, diagramsFor) {
      if (!groupIconsWrap) return;
      groupIconsWrap.innerHTML = "";
      groupIconsWrap.hidden = true;

      var models = Array.isArray(index && index.models) ? index.models : [];
      var model = models.find(function (m) {
        return safeText(m && m.code) === modelCode;
      });
      if (!model) return;

      var groups = Array.isArray(model.main_groups) ? model.main_groups.slice() : [];
      if (!groups.length) return;
      groups.sort(function (a, b) {
        return compareAlphaAsc(cleanGroupName(a.group_name), cleanGroupName(b.group_name));
      });

      groups.forEach(function (g) {
        var gCode = safeText(g.group_code);
        var diagrams = diagramsFor(modelCode, gCode);
        diagrams.sort(function (a, b) {
          return compareAlphaAsc(cleanDiagramTitle(a.title, a.illustration_id), cleanDiagramTitle(b.title, b.illustration_id));
        });
        var chip = createNode("button", "diagram-viewer__group-chip");
        chip.type = "button";
        if (gCode === groupCode) chip.classList.add("is-active");

        var icon = createGroupIcon(gCode, g.group_name, g.icon_url);
        icon.classList.add("diagram-viewer__group-icon");
        var label = createNode("span", "diagram-viewer__group-chip-label", cleanGroupName(g.group_name));
        chip.appendChild(icon);
        chip.appendChild(label);

        if (!diagrams.length) {
          chip.disabled = true;
          chip.classList.add("is-disabled");
        } else {
          chip.addEventListener("click", function () {
            window.location.assign(buildCatalogHref(modelCode, gCode));
          });
        }

        groupIconsWrap.appendChild(chip);
      });

      groupIconsWrap.hidden = false;
    }

    function findDiagramContext(index, key, id) {
      var models = Array.isArray(index && index.models) ? index.models : [];
      for (var i = 0; i < models.length; i++) {
        var model = models[i] || {};
        var groups = Array.isArray(model.main_groups) ? model.main_groups : [];
        for (var j = 0; j < groups.length; j++) {
          var group = groups[j] || {};
          var diagrams = Array.isArray(group.diagrams) ? group.diagrams.slice() : [];
          diagrams.sort(function (a, b) {
            return compareAlphaAsc(cleanDiagramTitle(a.title, a.illustration_id), cleanDiagramTitle(b.title, b.illustration_id));
          });

          for (var k = 0; k < diagrams.length; k++) {
            var d = diagrams[k] || {};
            var dKey = safeText(d.diagram_key);
            var dId = safeText(d.illustration_id);
            if (key) {
              if (dKey !== key) continue;
            } else if (dId !== id) {
              continue;
            }
            return {
              model: model,
              group: group,
              current: d,
              prev: k > 0 ? diagrams[k - 1] : null,
              next: k < diagrams.length - 1 ? diagrams[k + 1] : null
            };
          }
        }
      }
      return null;
    }

    function findFallbackDiagramContext(index, preferredModelCode, preferredGroupCode) {
      var models = Array.isArray(index && index.models) ? index.models.slice() : [];
      models.sort(function (a, b) {
        var bySort = toNumber(a && a.sort_order, 0) - toNumber(b && b.sort_order, 0);
        if (bySort !== 0) return bySort;
        return compareAlphaAsc(safeText(a && a.name), safeText(b && b.name));
      });

      function sortedGroups(model) {
        var groups = Array.isArray(model && model.main_groups) ? model.main_groups.slice() : [];
        groups.sort(function (a, b) {
          var bySort = toNumber(a && a.sort_order, 0) - toNumber(b && b.sort_order, 0);
          if (bySort !== 0) return bySort;
          return compareAlphaAsc(cleanGroupName(a && a.group_name), cleanGroupName(b && b.group_name));
        });
        return groups;
      }

      function sortedDiagrams(group) {
        var diagrams = Array.isArray(group && group.diagrams) ? group.diagrams.slice() : [];
        diagrams.sort(function (a, b) {
          var bySort = toNumber(a && a.sort_order, 0) - toNumber(b && b.sort_order, 0);
          if (bySort !== 0) return bySort;
          return compareAlphaAsc(safeText(a && a.illustration_id), safeText(b && b.illustration_id));
        });
        return diagrams;
      }

      function firstFromModel(model, forceGroupCode) {
        var groups = sortedGroups(model);
        if (forceGroupCode) {
          groups = groups.filter(function (g) {
            return safeText(g && g.group_code) === forceGroupCode;
          });
        }

        for (var gi = 0; gi < groups.length; gi++) {
          var group = groups[gi];
          var diagrams = sortedDiagrams(group);
          if (!diagrams.length) continue;
          return {
            model: model,
            group: group,
            current: diagrams[0]
          };
        }
        return null;
      }

      if (preferredModelCode) {
        var preferredModel = models.find(function (m) {
          return safeText(m && m.code) === preferredModelCode;
        });
        if (preferredModel) {
          var preferredHit = firstFromModel(preferredModel, preferredGroupCode);
          if (preferredHit) return preferredHit;
          var modelHit = firstFromModel(preferredModel, "");
          if (modelHit) return modelHit;
        }
      }

      for (var mi = 0; mi < models.length; mi++) {
        var anyHit = firstFromModel(models[mi], "");
        if (anyHit) return anyHit;
      }
      return null;
    }

    if (!diagramKey && !diagramId) {
      var preferredModelCode = safeText(params.get("model"));
      var preferredGroupCode = safeText(params.get("group"));
      if (indexUrl) {
        statusEl.textContent = "Loading diagram...";
        loadJSON(indexUrl, { preferFresh: true, retries: 2 })
          .then(function (index) {
            var fallback = findFallbackDiagramContext(index, preferredModelCode, preferredGroupCode);
            if (fallback && fallback.current) {
              window.location.replace(
                buildViewerSelfHref(
                  safeText(fallback.current.illustration_id),
                  safeText(fallback.current.diagram_key),
                  safeText(fallback.model && fallback.model.code),
                  safeText(fallback.group && fallback.group.group_code)
                )
              );
              return;
            }
            window.location.replace(buildCatalogHref(preferredModelCode, preferredGroupCode));
          })
          .catch(function () {
            window.location.replace(buildCatalogHref(preferredModelCode, preferredGroupCode));
          });
      } else {
        window.location.replace(buildCatalogHref(preferredModelCode, preferredGroupCode));
      }
      return;
    }

    function loadDiagram(key, id) {
      var baseLookup = key || id;
      if (!indexUrl) {
        if (!diagramJsonBase) {
          return Promise.reject(new Error("Missing index URL and base URL"));
        }
        return loadJSON(diagramUrlFromBase(baseLookup)).then(function (diagram) {
          return { diagram: diagram, context: null, index: null };
        });
      }

      return loadJSON(indexUrl, { preferFresh: true, retries: 2 }).then(function (index) {
        var context = findDiagramContext(index, key, id);
        if (context && context.current && context.current.data_url) {
          return loadJSON(context.current.data_url).then(function (diagram) {
            return { diagram: diagram, context: context, index: index };
          });
        }

        if (!diagramJsonBase) {
          throw new Error("Diagram not listed in index and no base URL configured");
        }

        return loadJSON(diagramUrlFromBase(baseLookup)).then(function (diagram) {
          return { diagram: diagram, context: null, index: index };
        });
      });
    }

    loadDiagram(diagramKey, diagramId)
      .then(function (payload) {
        renderDiagram(payload.diagram || {}, payload.context || null, payload.index || null);
      })
      .catch(function (err) {
        statusEl.textContent = "Diagram load failed: " + safeText(err && err.message);
      });

    function renderDiagram(diagram, context, index) {
      var title = safeText(diagram.title || diagram.illustration_id || diagramId || diagramKey);
      var cleanTitle = cleanDiagramTitle(title, safeText(diagram.illustration_id || diagramId || diagramKey));
      titleEl.textContent = cleanTitle || title;
      imageEl.src = safeText(diagram.diagram_url);
      imageEl.alt = title;
      statusEl.textContent = "";

      var modelCode = "";
      var groupCode = "";
      var modelName = safeText(diagram.model && diagram.model.name);
      var groupName = safeText(diagram.main_group && diagram.main_group.name);

      if (context) {
        modelCode = safeText(context.model && context.model.code);
        groupCode = safeText(context.group && context.group.group_code);
        modelName = safeText(context.model && context.model.name) || modelName;
        groupName = safeText(context.group && context.group.group_name) || groupName;
      } else {
        modelCode = safeText(diagram.model && diagram.model.code);
        groupCode = safeText(diagram.main_group && diagram.main_group.code);
      }

      if (breadcrumbPathEl) {
        breadcrumbPathEl.textContent =
          (modelName || "Model") +
          " > " +
          cleanGroupName(groupName || "Group") +
          " > " +
          cleanDiagramTitle(title, safeText(diagram.illustration_id || diagramId));
      }

      if (catalogLink) {
        catalogLink.href = buildCatalogHref(modelCode, groupCode);
      }

      if (context) {
        setNavLink(prevLink, context.prev, modelCode, groupCode);
        setNavLink(nextLink, context.next, modelCode, groupCode);
      } else {
        setNavLink(prevLink, null, "", "");
        setNavLink(nextLink, null, "", "");
      }

      if (index && jumpModel && jumpGroup && jumpDiagram) {
        var models = Array.isArray(index.models) ? index.models.slice() : [];
        models.sort(function (a, b) { return toNumber(a.sort_order, 0) - toNumber(b.sort_order, 0); });

        function setOpts(select, placeholder, options) {
          while (select.options.length) select.remove(0);
          var ph = createNode("option", "", placeholder);
          ph.value = "";
          select.appendChild(ph);
          options.forEach(function (o) {
            var op = createNode("option", "", safeText(o.label));
            op.value = safeText(o.value);
            select.appendChild(op);
          });
        }

        function groupsForModel(code) {
          var m = models.find(function (x) { return safeText(x.code) === code; });
          return m && Array.isArray(m.main_groups) ? m.main_groups.slice() : [];
        }

        function diagramsFor(modelCodeValue, groupCodeValue) {
          var groups = groupsForModel(modelCodeValue);
          var g = groups.find(function (x) { return safeText(x.group_code) === groupCodeValue; });
          return g && Array.isArray(g.diagrams) ? g.diagrams.slice() : [];
        }

        setOpts(jumpModel, "Model", models.map(function (m) { return { label: safeText(m.name), value: safeText(m.code) }; }));
        jumpModel.value = modelCode;

        var curGroups = groupsForModel(modelCode);
        curGroups.sort(function (a, b) {
          return compareAlphaAsc(cleanGroupName(a.group_name), cleanGroupName(b.group_name));
        });
        setOpts(jumpGroup, "Main group", curGroups.map(function (g) {
          return { label: cleanGroupName(g.group_name), value: safeText(g.group_code) };
        }));
        jumpGroup.value = groupCode;

        var curDiagrams = diagramsFor(modelCode, groupCode);
        curDiagrams.sort(function (a, b) {
          return compareAlphaAsc(cleanDiagramTitle(a.title, a.illustration_id), cleanDiagramTitle(b.title, b.illustration_id));
        });
        setOpts(jumpDiagram, "Diagram", curDiagrams.map(function (d) {
          var id = safeText(d.illustration_id);
          var key = safeText(d.diagram_key);
          return { label: cleanDiagramTitle(d.title, id), value: key || id };
        }));
        jumpDiagram.value = safeText(diagram.diagram_key || diagram.illustration_id || diagramKey || diagramId);

        jumpModel.onchange = function () {
          var g2 = groupsForModel(jumpModel.value);
          g2.sort(function (a, b) {
            return compareAlphaAsc(cleanGroupName(a.group_name), cleanGroupName(b.group_name));
          });
          setOpts(jumpGroup, "Main group", g2.map(function (g) {
            return { label: cleanGroupName(g.group_name), value: safeText(g.group_code) };
          }));
          jumpGroup.value = "";
          setOpts(jumpDiagram, "Diagram", []);
        };

        jumpGroup.onchange = function () {
          var d2 = diagramsFor(jumpModel.value, jumpGroup.value);
          d2.sort(function (a, b) {
            return compareAlphaAsc(cleanDiagramTitle(a.title, a.illustration_id), cleanDiagramTitle(b.title, b.illustration_id));
          });
          setOpts(jumpDiagram, "Diagram", d2.map(function (d) {
            var id = safeText(d.illustration_id);
            var key = safeText(d.diagram_key);
            return { label: cleanDiagramTitle(d.title, id), value: key || id };
          }));
        };

        jumpDiagram.onchange = function () {
          var nextKey = safeText(jumpDiagram.value);
          if (!nextKey) return;

          var sourceDiagrams = diagramsFor(jumpModel.value, jumpGroup.value);
          var selected = sourceDiagrams.find(function (d) {
            var dKey = safeText(d.diagram_key);
            var dId = safeText(d.illustration_id);
            return (dKey && dKey === nextKey) || (!dKey && dId === nextKey);
          });

          var nextId = safeText(selected && selected.illustration_id) || nextKey;
          var resolvedKey = safeText(selected && selected.diagram_key) || "";
          window.location.assign(buildViewerSelfHref(nextId, resolvedKey, jumpModel.value, jumpGroup.value));
        };

        renderGroupIcons(index, modelCode, groupCode, diagramsFor);
      } else if (groupIconsWrap) {
        groupIconsWrap.innerHTML = "";
        groupIconsWrap.hidden = true;
      }

      var partsRaw = Array.isArray(diagram.parts) ? diagram.parts : [];
      var calloutsRaw = Array.isArray(diagram.callouts) ? diagram.callouts : [];

      var parts = partsRaw
        .map(function (p) {
          var pos = safeText(p.pos);
          var posNorm = normalizePos(pos);
          var partNumber = safeText(p.part_number);
          return {
            pos: pos,
            posNorm: posNorm,
            partNumber: partNumber,
            partNumberCompact: compactPartNumber(partNumber),
            description: safeText(p.description),
            qty: safeText(p.qty),
            remarks: safeText(p.remarks),
            searchEnabled: p.search_enabled !== false,
            clickable: posNorm !== "" && posNorm !== "-"
          };
        })
        .filter(function (p) {
          // Drop heading/meta rows from some PET tables (no position and no part number).
          return !(p.posNorm === "" && p.partNumberCompact === "");
        });

      var validPositions = new Set(
        parts
          .filter(function (p) {
            return p.clickable;
          })
          .map(function (p) {
            return p.posNorm;
          })
      );

      var callouts = dedupeCallouts(calloutsRaw)
        .map(function (c) {
          return {
            idNorm: normalizePos(c.id),
            x: toNumber(c.x_pct, NaN),
            y: toNumber(c.y_pct, NaN)
          };
        })
        .filter(function (c) {
          return c.idNorm && Number.isFinite(c.x) && Number.isFinite(c.y) && validPositions.has(c.idNorm);
        });

      if (!callouts.length) {
        statusEl.textContent = "No hotspots available for this diagram.";
      }

      var rowsByPos = new Map();
      var hotspotsByPos = new Map();
      parts.forEach(function (p, idx) {
        if (!p.clickable) return;
        var arr = rowsByPos.get(p.posNorm) || [];
        arr.push(idx);
        rowsByPos.set(p.posNorm, arr);
      });
      callouts.forEach(function (c, idx) {
        var arr = hotspotsByPos.get(c.idNorm) || [];
        arr.push(idx);
        hotspotsByPos.set(c.idNorm, arr);
      });

      var rowEls = [];
      var hotspotEls = [];
      var selectedPos = "";

      function clearSelection() {
        rowEls.forEach(function (el) {
          el.classList.remove("is-selected");
        });
        hotspotEls.forEach(function (el) {
          el.classList.remove("is-selected", "is-unmatched");
        });
      }

      function firstPartNumberForPos(posNorm) {
        var indexes = rowsByPos.get(posNorm) || [];
        for (var i = 0; i < indexes.length; i++) {
          if (!parts[indexes[i]].searchEnabled) continue;
          var pn = parts[indexes[i]].partNumberCompact;
          if (pn) return pn;
        }
        return "";
      }

      function selectPos(posNorm, source) {
        selectedPos = posNorm;
        clearSelection();
        if (!posNorm) return;

        var rowIndexes = rowsByPos.get(posNorm) || [];
        var hsIndexes = hotspotsByPos.get(posNorm) || [];

        rowIndexes.forEach(function (idx) {
          if (rowEls[idx]) rowEls[idx].classList.add("is-selected");
        });

        if (source === "hotspot" && rowIndexes.length && rowEls[rowIndexes[0]]) {
          rowEls[rowIndexes[0]].scrollIntoView({ behavior: "smooth", block: "nearest" });
        }

        hsIndexes.forEach(function (idx) {
          if (hotspotEls[idx]) {
            hotspotEls[idx].classList.add("is-selected");
            if (!rowsByPos.has(posNorm)) hotspotEls[idx].classList.add("is-unmatched");
          }
        });

        if (!rowsByPos.has(posNorm)) {
          statusEl.textContent = "Hotspot " + posNorm + " has no matching part row.";
        } else {
          statusEl.textContent = "";
        }

        if (autoSearch && source === "hotspot") {
          var pn = firstPartNumberForPos(posNorm);
          if (pn) searchByPartNumber(pn);
        }
      }

      while (hotspotLayer.firstChild) hotspotLayer.removeChild(hotspotLayer.firstChild);
      callouts.forEach(function (c) {
        var btn = createNode("button", "diagram-viewer__hotspot");
        btn.type = "button";
        btn.style.left = c.x + "%";
        btn.style.top = c.y + "%";
        btn.setAttribute("aria-label", "Hotspot " + c.idNorm);
        btn.addEventListener("click", function () {
          selectPos(c.idNorm, "hotspot");
        });
        hotspotEls.push(btn);
        hotspotLayer.appendChild(btn);
      });

      while (tableBody.firstChild) tableBody.removeChild(tableBody.firstChild);
      parts.forEach(function (p) {
        var trClasses = [];
        trClasses.push(p.clickable ? "is-clickable" : "is-disabled");
        if (!p.searchEnabled) trClasses.push("is-no-search");
        var tr = createNode("tr", trClasses.join(" "));
        tr.appendChild(createNode("td", "", p.pos));

        var partTd = createNode("td", "col-part-number");
        if (p.partNumberCompact && p.searchEnabled) {
          var link = createNode("a", "diagram-viewer__part-link", p.partNumberCompact);
          link.href = "/search?q=" + encodeURIComponent(p.partNumberCompact);
          link.target = "_blank";
          link.rel = "noopener";
          link.addEventListener("click", function (evt) {
            evt.stopPropagation();
          });
          partTd.appendChild(link);
        } else if (p.partNumberCompact) {
          partTd.textContent = p.partNumberCompact;
        }
        tr.appendChild(partTd);

        tr.appendChild(createNode("td", "", p.description));
        tr.appendChild(createNode("td", "", p.qty));

        if (p.clickable) {
          tr.addEventListener("click", function () {
            selectPos(p.posNorm, "row");
          });
        }

        rowEls.push(tr);
        tableBody.appendChild(tr);
      });

    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    var catalogs = document.querySelectorAll("[data-diagram-catalog-root]");
    for (var i = 0; i < catalogs.length; i++) initCatalog(catalogs[i]);

    var viewers = document.querySelectorAll("[data-diagram-viewer-root]");
    for (var j = 0; j < viewers.length; j++) initViewer(viewers[j]);
  });
})();
